/**
 * One-page snapshot of the project: KPIs from every module, condensed
 * into a single scrollable view. Opens as a modal from ProjectView.
 */
import { X } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import {
  computeBreakEven,
  computePL,
  computeFiveYears,
  computeMonthlyProjection,
  year1Totals,
} from "@/lib/finance";

function Card({ label, value, hint, accent, testId }) {
  return (
    <div className="border border-[#E8E6E1] bg-white rounded-sm p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8886]">{label}</div>
      <div className={`mt-2 text-xl font-mono ${accent || "text-[#1A1A1A]"}`} style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
        {value ?? "—"}
      </div>
      {hint && <div className="mt-1 text-xs text-[#5C5B5A]">{hint}</div>}
    </div>
  );
}

function Section({ title, num, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline gap-3 border-t border-[#1A1A1A] pt-3">
        <span className="text-xs uppercase tracking-[0.3em] text-[#B89154] font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          {num}
        </span>
        <h3
          className="text-xl text-[#1A1A1A]"
          style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
        >
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

export default function OneSheet({ project, onClose }) {
  const { t, lang } = useLang();
  const m = project.modules || {};
  const concept = m.concept || {};
  const market = m.market || {};
  const ops = m.operations || {};
  const hr = m.hr || {};
  const cal = m.calendar || {};
  const mk = m.marketing || {};
  const bg = m.budget || {};
  const risks = m.risks || {};
  const canvas = m.canvas || {};
  const gya = m.gya || {};
  const segments = gya.segments || [];
  const partnersByCountry = gya.country_partners || {};
  const totalPartners = Object.values(partnersByCountry).reduce(
    (s, list) => s + (Array.isArray(list) ? list.length : 0),
    0
  );

  // Financial derivatives
  const totalStartup = (bg.startup_costs || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const totalFixed = (bg.fixed_monthly || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const totalVariable = (bg.variable_monthly || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const proj = computeMonthlyProjection({
    revenue_streams: bg.revenue_streams || [],
    totalMonthlyExpenses: totalFixed + totalVariable,
    totalStartup,
  });
  const totals = year1Totals(proj, totalFixed, totalVariable);
  const pl = computePL({
    revenue: totals.revenue,
    fixedExpenses: totals.fixedYearly,
    variableExpenses: totals.variableYearly,
    taxRate: parseFloat(bg.tax_rate) || 25,
  });
  const be = computeBreakEven({
    unitRevenue: parseFloat(bg.unit_avg_revenue) || 0,
    unitVariable: parseFloat(bg.unit_avg_variable_cost) || 0,
    totalFixedMonthly: totalFixed,
    monthlyProjection: proj,
  });
  const fy = computeFiveYears({
    y1Revenue: totals.revenue,
    y1Expenses: totals.expenses,
    taxRate: parseFloat(bg.tax_rate) || 25,
    revenueGrowth: parseFloat(bg.yearly_revenue_growth) || 0,
    expenseGrowth: parseFloat(bg.yearly_expense_growth) || 0,
  });
  const cum5y = fy.length ? fy[fy.length - 1].cumulative : 0;

  // HR totals
  const headcount = (hr.roles || []).reduce((s, r) => s + (parseInt(r.headcount) || 0), 0);

  // Operations totals
  const services = ops.services || [];
  const avgMargin = services.length
    ? services.reduce((s, x) => s + (parseFloat(x.margin) || 0), 0) / services.length
    : 0;

  // Marketing totals
  const totalAdSpend = (mk.digital_campaigns || []).reduce((s, c) => s + (parseFloat(c.budget) || 0), 0);
  const totalLeads = (mk.digital_campaigns || []).reduce((s, c) => s + (parseFloat(c.leads) || 0), 0);
  const actualCpl = totalLeads > 0 ? totalAdSpend / totalLeads : null;

  const fmt = (n) => `€${Math.round(n).toLocaleString()}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1A1A1A]/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
      onClick={onClose}
      data-testid="onesheet-overlay"
    >
      <div
        className="bg-[#F9F8F6] w-full max-w-5xl my-10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#F9F8F6] border-b border-[#E8E6E1] px-8 py-5 flex items-center justify-between z-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#B89154] font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              {t("onesheet.title")}
            </div>
            <h2
              className="text-3xl text-[#1A1A1A] mt-1"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
              data-testid="onesheet-project-title"
            >
              {project.name}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-[#8A8886] hover:text-[#1A1A1A] transition-colors"
            data-testid="onesheet-close-btn"
            aria-label="close"
          >
            <X className="h-6 w-6" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-8 py-8 space-y-10">
          {/* 01 — Concept snapshot */}
          <Section num="01" title={t("module.concept")}>
            <p className="text-sm text-[#1A1A1A] leading-relaxed max-w-3xl" data-testid="onesheet-idea">
              {concept.idea || "—"}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card label={t("concept.legal_form")} value={concept.legal_form || "—"} />
              <Card label={t("concept.location")} value={concept.location || "—"} />
              <Card label={lang === "es" ? "Socios" : "Partners"} value={(concept.partners || []).length} />
              <Card label={t("concept.mission")} value={concept.mission ? "✓" : "—"} />
            </div>
          </Section>

          {/* 02 — Canvas snapshot */}
          <Section num="02" title={t("module.canvas")}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Card label={t("canvas.value_propositions")} value={canvas.value_propositions ? "✓" : "—"} accent="text-[#B89154]" />
              <Card label={t("canvas.customer_segments")} value={canvas.customer_segments ? "✓" : "—"} accent="text-[#2563EB]" />
              <Card label={t("canvas.channels")} value={canvas.channels ? "✓" : "—"} accent="text-[#2563EB]" />
              <Card label={t("canvas.key_partners")} value={canvas.key_partners ? "✓" : "—"} accent="text-[#3A5A6B]" />
              <Card label={t("canvas.revenue_streams")} value={canvas.revenue_streams ? "✓" : "—"} accent="text-[#2563EB]" />
              <Card label={t("canvas.cost_structure")} value={canvas.cost_structure ? "✓" : "—"} accent="text-[#9E2A2B]" />
            </div>
          </Section>

          {/* 03 — Market snapshot */}
          <Section num="03" title={t("module.market")}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card label={t("market.competitors")} value={(market.competitors || []).length} />
              <Card label={t("market.personas")} value={(market.buyer_personas || []).length} />
              <Card label={t("swot.strengths")} value={market.swot?.strengths ? "✓" : "—"} accent="text-[#2563EB]" />
              <Card label={t("swot.threats")} value={market.swot?.threats ? "✓" : "—"} accent="text-[#3A5A6B]" />
            </div>
          </Section>

          {/* 04 — Operations + HR snapshot */}
          <Section num="04" title={`${t("module.operations")} · ${t("module.hr")}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card label={lang === "es" ? "Servicios" : "Services"} value={services.length} />
              <Card label={lang === "es" ? "Margen medio" : "Avg margin"} value={`${avgMargin.toFixed(1)}%`} accent="text-[#B89154]" />
              <Card label={lang === "es" ? "Roles" : "Roles"} value={(hr.roles || []).length} />
              <Card label={lang === "es" ? "Personas" : "Headcount"} value={headcount} accent="text-[#2563EB]" />
            </div>
          </Section>

          {/* 05 — Marketing snapshot */}
          <Section num="05" title={t("module.marketing")}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card label={t("marketing.target_cpl")} value={`€${mk.target_cpl || 0}`} />
              <Card label={t("marketing.target_cac")} value={`€${mk.target_cac || 0}`} />
              <Card label={lang === "es" ? "Inversión digital" : "Digital spend"} value={fmt(totalAdSpend)} />
              <Card label={lang === "es" ? "CPL real" : "Actual CPL"} value={actualCpl != null ? `€${actualCpl.toFixed(0)}` : "—"} accent={actualCpl != null && mk.target_cpl > 0 && actualCpl <= mk.target_cpl ? "text-[#2563EB]" : "text-[#9E2A2B]"} />
            </div>
          </Section>

          {/* 06 — Financial snapshot (highlight) */}
          <Section num="06" title={t("module.budget")}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card
                label={`${t("fin.revenue")} ${lang === "es" ? "Año 1" : "Y1"}`}
                value={fmt(pl.revenue)}
                accent="text-[#1A1A1A]"
                testId="onesheet-y1-revenue"
              />
              <Card
                label={`${t("fin.net_income")} ${lang === "es" ? "Año 1" : "Y1"}`}
                value={fmt(pl.netIncome)}
                hint={`${pl.netMarginPct.toFixed(1)}% ${lang === "es" ? "margen" : "margin"}`}
                accent={pl.netIncome >= 0 ? "text-[#2563EB]" : "text-[#9E2A2B]"}
                testId="onesheet-y1-net"
              />
              <Card
                label={t("fin.month_breakeven")}
                value={be.breakEvenMonth != null ? `M${be.breakEvenMonth}` : (lang === "es" ? "> 12m" : "> 12m")}
                hint={be.customersPerMonth != null ? `${Math.ceil(be.customersPerMonth)} ${lang === "es" ? "clientes/mes" : "customers/mo"}` : null}
                accent="text-[#B89154]"
                testId="onesheet-be-month"
              />
              <Card
                label={lang === "es" ? "Acumulado 5 años" : "5-yr cumulative"}
                value={fmt(cum5y)}
                accent={cum5y >= 0 ? "text-[#2563EB]" : "text-[#9E2A2B]"}
                testId="onesheet-5y-cumulative"
              />
            </div>
          </Section>

          {/* 07 — Risks snapshot */}
          <Section num="07" title={t("module.risks")}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card label={lang === "es" ? "Riesgos totales" : "Total risks"} value={(risks.items || []).length} />
              <Card label={lang === "es" ? "Alto impacto" : "High impact"} value={(risks.items || []).filter((r) => ["Alto", "High"].includes(r.impact)).length} accent="text-[#9E2A2B]" />
              <Card label={lang === "es" ? "Hitos anuales" : "Annual milestones"} value={(cal.annual_milestones || []).length} />
              <Card label={lang === "es" ? "Plan 5 años" : "5-year plan"} value={(cal.five_year_plan || []).length} accent="text-[#2563EB]" />
            </div>
          </Section>

          {/* 08 — GYA specialized snapshot */}
          {segments.length > 0 && (
            <Section num="08" title={t("module.gya")}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card
                  label={lang === "es" ? "Segmentos" : "Segments"}
                  value={segments.length}
                  hint={segments.map((s) => s.name).join(" · ")}
                  accent="text-[#B89154]"
                  testId="onesheet-gya-segments"
                />
                <Card
                  label={lang === "es" ? "Países" : "Countries"}
                  value={Object.keys(partnersByCountry).length}
                  hint={Object.keys(partnersByCountry).join(" · ")}
                />
                <Card
                  label={lang === "es" ? "Partners" : "Partners"}
                  value={totalPartners}
                  hint={lang === "es" ? "Red local certificada" : "Certified local network"}
                />
                <Card
                  label={t("gya.funnel.blended_ratio")}
                  value={`${(gya.funnel_summary?.blended_ltv_cac || 0).toFixed(1)}×`}
                  hint={`LTV €${(gya.funnel_summary?.blended_ltv || 0).toLocaleString()} / CAC €${(gya.funnel_summary?.blended_cac || 0).toLocaleString()}`}
                  accent="text-[#2563EB]"
                  testId="onesheet-gya-ratio"
                />
              </div>
            </Section>
          )}

          <div className="text-center text-xs text-[#8A8886] tracking-[0.2em] uppercase font-mono pt-6 border-t border-[#E8E6E1]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            Atelier Business · {new Date().toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </div>
      </div>
    </div>
  );
}
